import React, { useCallback, useEffect, useRef } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "components/common/Sheet";

export interface ModernSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "full";
  className?: string;
  /** When true, ignore Escape / outside click / close button dismiss. */
  preventDismiss?: boolean;
  /** Hide the default top-right close control (e.g. while a mutation is pending). */
  hideCloseButton?: boolean;
}

const sizeClasses = {
  sm: "w-[400px]",
  md: "w-[600px]",
  lg: "w-[800px]",
  xl: "w-[1000px]",
  full: "w-full",
};

const focusableSelectors = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
  "[contenteditable='true']",
].join(",");

const isVisible = (element: HTMLElement) => {
  if (element.hidden) return false;
  const style = window.getComputedStyle(element);
  if (style.display === "none" || style.visibility === "hidden") return false;
  if (style.pointerEvents === "none") return false;

  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

const getFocusableElements = (container: HTMLElement) => {
  const elements = Array.from(container.querySelectorAll<HTMLElement>(focusableSelectors));

  return elements.filter((element) => {
    if (element.hasAttribute("data-radix-focus-guard")) {
      return false;
    }

    return isVisible(element);
  });
};

export const ModernSheet: React.FC<ModernSheetProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  className = "",
  preventDismiss = false,
  hideCloseButton = false,
}) => {
  const contentRef = useRef<HTMLDivElement | null>(null);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        if (preventDismiss) {
          return;
        }
        onClose();
      }
    },
    [onClose, preventDismiss]
  );

  const blockDismissEvent = useCallback(
    (event: { preventDefault: () => void }) => {
      if (preventDismiss) {
        event.preventDefault();
      }
    },
    [preventDismiss]
  );

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key !== "Tab" || event.defaultPrevented) {
      return;
    }

    const container = contentRef.current;
    if (!container) return;

    const target = event.target as HTMLElement | null;
    const isFocusGuard = Boolean(target?.hasAttribute("data-radix-focus-guard"));

    if (!isFocusGuard && (!target || !container.contains(target))) {
      return;
    }

    const focusableElements = getFocusableElements(container);
    if (focusableElements.length === 0) return;

    const activeElement = document.activeElement as HTMLElement | null;
    const currentIndex = activeElement ? focusableElements.indexOf(activeElement) : -1;

    if (focusableElements.length === 1) {
      event.preventDefault();
      focusableElements[0].focus();
      return;
    }

    let nextIndex: number;

    if (currentIndex === -1) {
      nextIndex = event.shiftKey ? focusableElements.length - 1 : 0;
    } else if (event.shiftKey) {
      nextIndex = currentIndex === 0 ? focusableElements.length - 1 : currentIndex - 1;
    } else {
      nextIndex = currentIndex === focusableElements.length - 1 ? 0 : currentIndex + 1;
    }

    const nextElement = focusableElements[nextIndex];
    if (nextElement) {
      event.preventDefault();
      nextElement.focus();
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const listenerOptions: AddEventListenerOptions = { capture: true };
    document.addEventListener("keydown", handleKeyDown, listenerOptions);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, listenerOptions);
    };
  }, [handleKeyDown, isOpen]);

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent
        ref={contentRef}
        className={`${sizeClasses[size]} ${className}`}
        showCloseButton={!hideCloseButton}
        onPointerDownOutside={blockDismissEvent}
        onInteractOutside={blockDismissEvent}
        onEscapeKeyDown={blockDismissEvent}
      >
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          {description && <SheetDescription>{description}</SheetDescription>}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">{children}</div>

        {footer && <SheetFooter>{footer}</SheetFooter>}
      </SheetContent>
    </Sheet>
  );
};
